'use client';

import * as Sentry from '@sentry/react';
import { scan } from 'react-scan';
import { createRoot } from 'react-dom/client';
import 'component-library/static/css/base.css';
import { QueryClientProvider } from '@tanstack/react-query';

// Import the generated route tree
import { RouterProvider } from 'react-router/dom';
import { CsrdSubpagePersistenceProvider } from 'csrd/csrd-subpage-persistence-provider';
import { createQueryClient } from 'core/lib/queryClient/createQueryClient';
import { initSentry } from 'core/GovernanceProvider/initSentry';
import { Auth0ProviderWithHistory } from 'core/AuthProvider';
import { GovernanceProvider } from 'core/GovernanceProvider';
import { StrictMode } from 'core/lib/utils/StrictMode';
import { ProfileProvider } from 'core/lib/Profile';
import { SubscriptionsProvider } from 'core/lib/Subscriptions';
import { LocationsProvider } from 'core/lib/Locations';
import { ServiceProvider } from 'core/lib/Services';
import { EventTrackingProvider } from 'core/lib/hooks/useEventTracking/useEventTracking';
import PersistedEntitiesProvider from 'core/PersistedEntitiesContext';
import { ErrorPage } from '#components/Error/index.js';
import { RequireAuthentication } from '#components/RequireAuthentication';
import { BannerProvider } from '../../packages/core/src/lib/SiteBanner/BannerProvider';
import { router } from './routes/routeTree';

// Init Sentry as early as possible
initSentry(
  'https://f405b60c8a09478ca68cef19d8e84e92@o481031.ingest.sentry.io/4504083514130432',
  __SENTRY_RELEASE__ || undefined,
);

scan({
  enabled: import.meta.env.DEV && import.meta.env.PORTAL_DEBUG === 'true',
});

// Render the app
const container = document.getElementById('root');

if (!container) {
  throw Error('Failed to find the root element');
}

const root = createRoot(container);

const queryClient = createQueryClient();

const errorPage = <ErrorPage errorType="SOMETHING_WENT_WRONG" />;
const authenticatedErrorPage = (
  <ErrorPage errorType="SOMETHING_WENT_WRONG" includeAppContainer={true} />
);

root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={errorPage}>
      <Auth0ProviderWithHistory>
        <RequireAuthentication>
          <QueryClientProvider client={queryClient}>
            <ProfileProvider>
              <GovernanceProvider
                errorPage={errorPage}
                authenticatedErrorPage={authenticatedErrorPage}
              >
                <LocationsProvider>
                  <ServiceProvider>
                    <EventTrackingProvider>
                      <PersistedEntitiesProvider>
                        <SubscriptionsProvider>
                          <BannerProvider>
                            <CsrdSubpagePersistenceProvider>
                              <RouterProvider router={router} />
                            </CsrdSubpagePersistenceProvider>
                          </BannerProvider>
                        </SubscriptionsProvider>
                      </PersistedEntitiesProvider>
                    </EventTrackingProvider>
                  </ServiceProvider>
                </LocationsProvider>
              </GovernanceProvider>
            </ProfileProvider>
          </QueryClientProvider>
        </RequireAuthentication>
      </Auth0ProviderWithHistory>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
